import { IObservable, Subject } from '~/rage/utils';
import { IClientConfigs } from '~/shared';
import { BackendService } from '../BackendService';
import { RpcService } from '../RpcService';

export class GameConfigService {
	private initPromise: Promise<void> | null = null;
	private initResolve: (() => void) | null = null;

	private readonly _configs = new Subject<Partial<IClientConfigs>>({});
	private lastUpdatedAt = 0;
	private isInitialized = false;

	private FETCH_ATTEMPT_DELAY_MS = 1000;
	private retryCount = 0;

	private updateListeners = new Map<string, Set<(config: any) => void>>();

	private pendingUpdates: Array<{
		ts: number;
		configs: IClientConfigs | Partial<IClientConfigs>;
	}> = [];

	constructor(
		private readonly rpc: RpcService,
		private readonly backend: BackendService,
	) {
		this.initPromise = new Promise((resolve) => {
			this.initResolve = resolve;
		});

		this.rpc.register(
			'gameConfig:fetch',
			this.handleFetchFromClient.bind(this),
		);

		this.backend.addEventHandler(
			'gameConfig:update',
			this.handleUpdate.bind(this),
		);
		this.backend.waitForInit().then(() => this.fetch());
	}

	public async waitForInit(): Promise<void> {
		await this.initPromise;
	}

	private handleFetchFromClient(): void {
		this.fetch();
	}

	public get configs(): Partial<IClientConfigs> {
		return this._configs.getValue();
	}

	public getConfigs(): IObservable<Partial<IClientConfigs>> {
		return this._configs;
	}

	public async fetch(): Promise<void> {
		await this.backend.waitForInit();

		try {
			await this.backend.invoke('gameConfig:fetch', {
				ts: this.lastUpdatedAt,
			});
			this.retryCount = 0;
		} catch (err: any) {
			this.retryCount++;
			console.error(
				`[GameConfigService] Fetch failed (attempt ${this.retryCount}), retrying...`,
				err.message,
			);
			setTimeout(() => this.fetch(), this.FETCH_ATTEMPT_DELAY_MS);

			return;
		}
	}

	private handleUpdate(payload: {
		ts: number;
		configs: IClientConfigs | Partial<IClientConfigs> | null;
	}) {
		this.lastUpdatedAt = payload.ts;

		if (!payload.configs) {
			return;
		}

		if (!this.isInitialized) {
			this.pendingUpdates.push(
				payload as {
					ts: number;
					configs: IClientConfigs | Partial<IClientConfigs>;
				},
			);
			this.flushPendingUpdates();
			return;
		}

		this.applyUpdate(
			payload as {
				ts: number;
				configs: IClientConfigs | Partial<IClientConfigs>;
			},
		);
	}

	private flushPendingUpdates() {
		if (this.pendingUpdates.length === 0) return;

		const first = this.pendingUpdates.shift()!;
		this._configs.next(first.configs as IClientConfigs);
		this.isInitialized = true;

		const changedKeys = Object.keys(first.configs) as (keyof IClientConfigs)[];
		console.log(
			`[GameConfigService] Initialized with ${
				changedKeys.length
			} key(s): ${changedKeys.join(', ')}`,
		);

		this.initResolve?.();
		this.initResolve = null;
		this.initPromise = null;

		this.fireOnUpdateListeners(changedKeys);

		while (this.pendingUpdates.length > 0) {
			const next = this.pendingUpdates.shift()!;
			this.applyUpdate(next);
		}
	}

	private applyUpdate(payload: {
		ts: number;
		configs: IClientConfigs | Partial<IClientConfigs>;
	}) {
		const currentConfigs = this._configs.getValue();
		if (currentConfigs) {
			this._configs.next({
				...currentConfigs,
				...payload.configs,
			} as IClientConfigs);
		} else {
			this._configs.next(payload.configs as IClientConfigs);
		}

		const changedKeys = Object.keys(
			payload.configs,
		) as (keyof IClientConfigs)[];
		console.log(
			`[GameConfigService] Updated with ${
				changedKeys.length
			} key(s): ${changedKeys.join(', ')}`,
		);

		this.fireOnUpdateListeners(changedKeys);
	}

	public onUpdate<K extends keyof IClientConfigs>(
		key: K,
		callback: (config: IClientConfigs[K]) => void,
	): () => void {
		if (!this.updateListeners.has(key)) {
			this.updateListeners.set(key, new Set());
		}
		this.updateListeners.get(key)!.add(callback);

		return () => {
			this.updateListeners.get(key)?.delete(callback);
		};
	}

	public async waitForConfig<K extends keyof IClientConfigs>(
		key: K,
		timeoutMs?: number,
	): Promise<IClientConfigs[K] | null> {
		const currentConfigs = this._configs.getValue();
		if (currentConfigs && key in currentConfigs) {
			return currentConfigs[key] as IClientConfigs[K] | null;
		}

		return new Promise<IClientConfigs[K] | null>((resolve, reject) => {
			let timeout: NodeJS.Timeout | undefined;

			const unsub = this.onUpdate(key, (config: unknown) => {
				clearTimeout(timeout);
				resolve(config as IClientConfigs[K]);
			});

			if (timeoutMs) {
				timeout = setTimeout(() => {
					unsub();
					reject(
						new Error(
							`[GameConfigService] waitForConfig('${key}') timed out after ${timeoutMs}ms`,
						),
					);
				}, timeoutMs);
			}
		});
	}

	public async waitForConfigs<K extends keyof IClientConfigs>(
		keys: K[],
		timeoutMs?: number,
	): Promise<Pick<IClientConfigs, K>> {
		await Promise.all(keys.map((key) => this.waitForConfig(key, timeoutMs)));

		const currentConfigs = this._configs.getValue();
		if (!currentConfigs) {
			throw new Error('Configs not loaded');
		}

		return currentConfigs as Pick<IClientConfigs, K>;
	}

	private fireOnUpdateListeners(changedKeys: (keyof IClientConfigs)[]) {
		const currentConfigs = this._configs.getValue();
		for (const key of changedKeys) {
			const listeners = this.updateListeners.get(key);
			if (listeners && currentConfigs && key in currentConfigs) {
				const config = (currentConfigs as any)[key];
				for (const fn of listeners) {
					try {
						fn(config);
					} catch (err) {
						console.error(
							`[GameConfigService] onUpdate listener for "${key}" threw:`,
							err,
						);
					}
				}
			}
		}
	}
}
